import React, {
    useContext, useEffect, useState, useCallback
} from 'react';
import _ from 'lodash';
import { readViewModelValue } from 'gw-jutro-adapters-react';
import { WizardPage, wizardProps, WizardPageTemplate } from 'gw-portals-wizard-react';
import {
    ViewModelServiceContext,
    withViewModelService,
    ViewModelForm
} from 'gw-portals-viewmodel-react';
import { TranslatorContext } from '@jutro/locale';
import { useValidation } from 'gw-portals-validation-react';
import Claim from '../../models/Claim';
import Address from '../../models/Address';
import metadata from './ContactDetailsPage.metadata.json5';
import styles from './ContactDetailsPage.module.scss';
import messages from '../../FNOL.messages';
import AddressComponent from "../../../nexus-capability-gateway-react/Components/Address/AddressComponent";

function FNOLContactDetailsPage(props) {
    const translator = useContext(TranslatorContext);
    const viewModelService = useContext(ViewModelServiceContext);
    const { wizardData: claimVM, updateWizardData } = props;
    const [availableContacts, setAvailableContacts] = useState([]);
    const [mainContactValue, updateMainContactValue] = useState('');
    const [locState, updateLocState] = useState('');
    const newContact = {
        placeholder: true
    };
    const {
        onValidate,
        isComponentValid,
        initialValidation,
        registerComponentValidation
    } = useValidation('FNOLContactDetailsPage');
    const relationToInsuredTypelistValues = claimVM.relationToInsured.aspects.availableValues
        .map((typeCode) => {
            return {
                code: typeCode.code,
                name: translator({
                    id: typeCode.name,
                    defaultMessage: typeCode.name
                })
            };
        });

    const calcualtelossLocations = useCallback(()=>{
        let derivedLoc= [];
        if(claimVM.policy.lobs?.homeowner?.locations){
            derivedLoc = claimVM.policy.lobs?.homeowner?.locations?.value.map((c) => {
                return {
                    code : c.locationAddress.publicID,
                    name : c.locationAddress.displayName
                }
        })
        }
        if(claimVM.policy.lobs?.farm?.locations){
            derivedLoc = claimVM.policy.lobs?.farm?.locations?.value.map((c) => {
                return {
                    code : c.address.publicID,
                    name : c.address.displayName
                }
        })
        }
        if(claimVM.policy.lobs?.businessOwners?.locations){
            derivedLoc = claimVM.policy.lobs?.businessOwners?.locations?.value.map((c) => {
                return {
                    code : c.address.publicID,
                    name : c.address.displayName
                }
        })
        }
        return derivedLoc;
    },[claimVM]);

    const onHandleLocationChange = useCallback((value, path) => {
        let locationObj = {};
        if(claimVM.policy.lobs.homeowner.value){
             locationObj = claimVM.policy.lobs.homeowner.locations?.value.find((elm)=>{
                return elm.locationAddress.publicID === value;
            });
        }
        if(claimVM.policy.lobs.farm.value){
             locationObj = claimVM.policy.lobs.farm.locations?.value.find((elm)=>{
                return elm.address.publicID === value;
            });
        }
        if(claimVM.policy.lobs.businessOwners.value){
             locationObj = claimVM.policy.lobs.businessOwners.locations?.value.find((elm)=>{
                return elm.address.publicID === value;
            });
        }   
            updateLocState(value);
            if(locationObj != undefined){
                if(claimVM.policy.lobs.homeowner.value){
                    _.set(claimVM, "lossLocation", locationObj.locationAddress);
                }else{
                    _.set(claimVM, "lossLocation", locationObj.address);
                }  
            }else{
                _.set(claimVM, "lossLocation", {});
            }
            updateWizardData(claimVM);
        },[claimVM]);

    const getAvailableContacts = useCallback(
        (newClaimVM) => {
            const contactsData = newClaimVM.value
                .availableContacts()
                .filter((contact) => contact.subtype === 'Person');
            contactsData.push(newContact);
            const dropDownContacts = contactsData.map((contact) => {
                const val = contact.placeholder
                    ? translator(messages.otherPerson)
                    : contact.getDisplayName(translator(messages.newContact));
                return {
                    code: val,
                    name: val
                };
            });
            return dropDownContacts;
        },
        [newContact, translator]
    );

    const initializeVM = useCallback(() => {
        const submittingClaimVM = viewModelService.create(
            new Claim(claimVM.value),
            'cc',
            'edge.capabilities.claim.fnol.dto.FnolDTO',
            {
                SubmittingClaim: true
            }
        );
        const newClaimVM = viewModelService.clone(submittingClaimVM);
        updateWizardData(newClaimVM);
        return newClaimVM;
    }, [claimVM, updateWizardData, viewModelService]);

    const isContactNumberCheck = useCallback(() => {
        if (claimVM.mainContact.aspects.valid && claimVM.mainContact.aspects.subtreeValid) {
            return true;
        }
        return false;
    }, [claimVM]);

    const isReporterContactValid = useCallback(() => {
        if (claimVM.reporter.aspects.valid && claimVM.reporter.aspects.subtreeValid) {
            return true;
        }
        return false;
    }, [claimVM]);

    useEffect(
        () => {
            const newClaimVM = initializeVM();
            if (_.isEmpty(newClaimVM.value.mainContact)) {
                if(newClaimVM.value.contacts.length > 0){
                    newClaimVM.value.mainContact = newClaimVM.contacts.value[0];
                }
                else {
                    newClaimVM.value.mainContact = newClaimVM.value.createContact();
                    newClaimVM.value.mainContact.primaryPhoneType = 'work';
                    newClaimVM.value.mainContact.displayName = translator(messages.newContact);
                    newClaimVM.value.mainContact.primaryAddress = new Address();
                }
            }

            if (_.isEmpty(newClaimVM.value.reporter)) {
                if(newClaimVM.value.contacts.length > 0){
                    newClaimVM.value.reporter = newClaimVM.contacts.value[0];
                }
                else {
                    newClaimVM.value.reporter = newClaimVM.value.createContact();
                    newClaimVM.value.reporter.primaryPhoneType = 'work';
                    newClaimVM.value.reporter.displayName = translator(messages.newContact);
                    newClaimVM.value.reporter.primaryAddress = new Address();
                }
            }

            const getContactList = getAvailableContacts(newClaimVM);
            setAvailableContacts(getContactList);
            updateMainContactValue(newClaimVM.value.ismainContactSameAsReporter);
        },
        // execute once
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    useEffect(() => {
        registerComponentValidation(isContactNumberCheck);
        registerComponentValidation(isReporterContactValid);
    }, [isContactNumberCheck, isReporterContactValid, registerComponentValidation]);

    const handleContactChange = useCallback(
        (value) => {
            if(!value) return;
            if (value === 'Other Person') {
                claimVM.mainContact.value = claimVM.value.createContact();
                claimVM.mainContact.primaryPhoneType.value = 'work';
                claimVM.mainContact.displayName.value = translator(messages.newContact);
                claimVM.mainContact.primaryAddress.value = new Address();
                const getContactList = getAvailableContacts(claimVM);
                setAvailableContacts(getContactList);

            }
            if (value !== 'Other Person') {
                const selectedMainContact = _.filter(claimVM.value.contacts, (contact) => {
                    const contactName = contact.displayName
                        ? contact.displayName
                        : contact.getDisplayName();
                    return contactName === value;
                });
                if (selectedMainContact.length > 0) {
                    selectedMainContact[0].primaryAddress = !_.isEmpty(
                        selectedMainContact[0].primaryAddress
                    )
                        ? selectedMainContact[0].primaryAddress
                        : new Address();
                    selectedMainContact[0].displayName = !_.isEmpty(selectedMainContact[0].displayName)
                        ? selectedMainContact[0].displayName
                        : selectedMainContact[0].getDisplayName();
                    _.set(claimVM, 'mainContact.value', selectedMainContact[0]);
                    updateWizardData(claimVM);
                }
            }
        },
        [claimVM, getAvailableContacts, translator, updateWizardData]
    );

    const handleNameChange = useCallback(
        (value, path) => {
            _.set(claimVM, path, value);
            const selectedContactArray = _.filter(claimVM.contacts.value, (contact) => {
                if ('tempID' in contact) {
                    if (contact.tempID === claimVM.mainContact.tempID.value) {
                        return contact;
                    }
                }
                return null;
            });
            const newDisplayName = selectedContactArray[0].getDisplayName(
                translator(messages.newContact)
            );
            _.set(claimVM, 'mainContact.displayName.value', newDisplayName);
            updateWizardData(claimVM);
            const addNew = getAvailableContacts(claimVM);
            setAvailableContacts(addNew);
        },
        [claimVM, getAvailableContacts, translator, updateWizardData]
    );

    const getAddressLineFields = useCallback(() => {
        if (claimVM.value.mainContact && claimVM.value.mainContact.primaryAddress) {
            return (
                <div>
                    <p>{claimVM.value.mainContact.primaryAddress.addressLine1}</p>
                    <p>{claimVM.value.mainContact.primaryAddress.addressLine2}</p>
                    <p>{claimVM.value.mainContact.primaryAddress.addressLine3}</p>
                </div>
            );
        }
        return '';
    }, [claimVM]);

    const onAddressChange = (value, path) => {
        _.set(claimVM.value, `mainContact.primaryAddress.${path}`, value);
        updateWizardData(claimVM);
    };

    const handleReporterContactChange = useCallback(
        (value) => {
            if(!value) return;
            if (value === 'Other Person') {
                claimVM.reporter.value = claimVM.value.createContact();
                claimVM.reporter.primaryPhoneType.value = 'work';
                claimVM.reporter.displayName.value = translator(messages.newContact);
                claimVM.reporter.primaryAddress.value = new Address();
                const getContactList = getAvailableContacts(claimVM);
                setAvailableContacts(getContactList);

            }
            if (value !== 'Other Person') {
                const selectedMainContact = _.filter(claimVM.value.contacts, (contact) => {
                    const contactName = contact.displayName
                        ? contact.displayName
                        : contact.getDisplayName();
                    return contactName === value;
                });
                if (selectedMainContact.length > 0) {
                    selectedMainContact[0].primaryAddress = !_.isEmpty(
                        selectedMainContact[0].primaryAddress
                    )
                        ? selectedMainContact[0].primaryAddress
                        : new Address();
                    selectedMainContact[0].displayName = !_.isEmpty(selectedMainContact[0].displayName)
                        ? selectedMainContact[0].displayName
                        : selectedMainContact[0].getDisplayName();
                    _.set(claimVM, 'reporter.value', selectedMainContact[0]);
                    updateWizardData(claimVM);
                }
            }
        },
        [claimVM, getAvailableContacts, translator, updateWizardData]
    );

    const onAddressChangeForReporter = (value, path) => {
        _.set(claimVM.value, `reporter.primaryAddress.${path}`, value);
        updateWizardData(claimVM);
    };

    const handleReporterNameChange = useCallback(
        (value, path) => {
            _.set(claimVM, path, value);
            const selectedContactArray = _.filter(claimVM.contacts.value, (contact) => {
                if ('tempID' in contact) {
                    if (contact.tempID === claimVM.reporter.tempID.value) {
                        return contact;
                    }
                }
                return null;
            });
            const newDisplayName = selectedContactArray[0].getDisplayName(
                translator(messages.newContact)
            );
            _.set(claimVM, 'reporter.displayName.value', newDisplayName);
            updateWizardData(claimVM);
            const addNew = getAvailableContacts(claimVM);
            setAvailableContacts(addNew);
        },
        [claimVM, getAvailableContacts, translator, updateWizardData]
    );

    const handleValueChange = useCallback(
        (value) => {
            updateMainContactValue(value);
            _.set(claimVM, 'ismainContactSameAsReporter.value', value);
            updateWizardData(claimVM);
        },
        [claimVM, updateWizardData]
    );

    const overrides = {
        '@field': {
            showOptional: false,
            showRequired: true
        },
        // workNumberField: {
        //     required: _.get(claimVM, 'mainContact.primaryPhoneType.value.code') === 'work'
        // },
        // homeNumberField: {
        //     required: _.get(claimVM, 'mainContact.primaryPhoneType.value.code') === 'home'
        // },
        // mobileNumberField: {
        //     required: _.get(claimVM, 'mainContact.primaryPhoneType.value.code') === 'mobile'
        // },
        fnolContactField: {
            availableValues: (availableContacts.length > 2) && !(isComponentValid) ?
            availableContacts.filter((item) => {
                return item.code != translator(messages.otherPerson)
            }) : availableContacts,
            onValueChange: handleContactChange,
            showOptional: false,
            defaultValue: availableContacts.length > 0 ? availableContacts[0].code : ''
        },
        firstNameField: {
            visible: !(_.get(claimVM, 'mainContact.policyRole.value.code') === 'insured'),
            onValueChange: handleNameChange
        },
        lastNameField: {
            visible: !(_.get(claimVM, 'mainContact.policyRole.value.code') === 'insured'),
            onValueChange: handleNameChange
        },
        addressField: {
            visible: _.get(claimVM, 'mainContact.policyRole.value.code') === 'insured',
            value: getAddressLineFields()
        },
        addressLine1Field: {
            visible: !(_.get(claimVM, 'mainContact.policyRole.value.code') === 'insured')
        },
        addressLine2Field: {
            visible: !(_.get(claimVM, 'mainContact.policyRole.value.code') === 'insured')
        },
        addressLine3Field: {
            visible: !(_.get(claimVM, 'mainContact.policyRole.value.code') === 'insured')
        },
        newContactCityField: {
            readOnly: _.get(claimVM, 'mainContact.policyRole.value.code') === 'insured'
        },
        newContactPostalCodeField: {
            readOnly: _.get(claimVM, 'mainContact.policyRole.value.code') === 'insured'
        },
        newContactStateField: {
            readOnly: _.get(claimVM, 'mainContact.policyRole.value.code') === 'insured'
        },
        lossAddressComponent: {
            model: claimVM.mainContact.primaryAddress,
            //hideFields: ['addressLine3'],
            onValueChange: onAddressChange
        },
        fnolContactRelationToInsured: {
            availableValues: relationToInsuredTypelistValues,
            onValueChange: (value) => {
                _.set(claimVM, 'relationToInsured.value', value);
                updateWizardData(claimVM);
            },
            value: claimVM.relationToInsured.value ? claimVM.relationToInsured.value.code : undefined
        },
        fnolReporterContactField: {
            availableValues: (availableContacts.length > 2) && !(isComponentValid) ?
                availableContacts.filter((item) => {
                    return item.code != translator(messages.otherPerson)
                }) : availableContacts,
            onValueChange: handleReporterContactChange,
            showOptional: false,
            defaultValue: availableContacts.length > 0 ? availableContacts[0].code : ''
        },
        reporterFirstNameField: {
            visible: !(_.get(claimVM, 'reporter.policyRole.value.code') === 'insured'),
            onValueChange: handleReporterNameChange
        },
        reporterLastNameField: {
            visible: !(_.get(claimVM, 'reporter.policyRole.value.code') === 'insured'),
            onValueChange: handleReporterNameChange
        },
        reporterWorkNumberField: {
            required: _.get(claimVM, 'reporter.primaryPhoneType.value.code') === 'work'
        },
        reporterHomeNumberField: {
            required: _.get(claimVM, 'reporter.primaryPhoneType.value.code') === 'home'
        },
        reporterMobileNumberField: {
            required: _.get(claimVM, 'reporter.primaryPhoneType.value.code') === 'mobile'
        },
        reporterAddressComponent: {
            model: claimVM.reporter.primaryAddress,
            //hideFields: ['addressLine3'],
            onValueChange: onAddressChangeForReporter
        },
        fnolReporterContactRelationToInsured: {
            availableValues: relationToInsuredTypelistValues,
            onValueChange: (value) => {
                _.set(claimVM, 'reporterRelationToInsured.value', value);
                updateWizardData(claimVM);
            },
            value: claimVM.reporterRelationToInsured.value ? claimVM.reporterRelationToInsured.value.code : undefined
        },
        mainContactRadioButton: {
            value: mainContactValue
        },
        mainContactDetailsContainer: {
            visible: (mainContactValue === false)
                || (claimVM.reporter && claimVM.ismainContactSameAsReporter === false)
        },
        lossLocation: {
            visible: claimVM.policy.lobs.homeowner.value != undefined || claimVM.policy.lobs.farm.value != undefined || claimVM.policy.lobs.businessOwners.value != undefined,
            required: claimVM.policy.lobs.homeowner.value != undefined || claimVM.policy.lobs.farm.value != undefined || claimVM.policy.lobs.businessOwners.value != undefined,
            availableValues : calcualtelossLocations(),
            onValueChange: onHandleLocationChange,
            value: locState
        },
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveComponentMap: {
            addressComponent: AddressComponent
        },
        resolveCallbackMap: {
            handleValueChange: handleValueChange
        }
    };

    const readValue = useCallback(
        (id, path) => {
            return readViewModelValue(metadata.pageContent, claimVM, id, path, overrides);
        },
        [claimVM, overrides]
    );

    // if (_.isEmpty(claimVM.value.mainContact)) {
    //     return null;
    // }

    return (
        <WizardPage
            cancelLabel={translator(messages.fnolSaveandExit)}
            disableNext={!isComponentValid}
            skipWhen={initialValidation}
            template={WizardPageTemplate}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={claimVM}
                resolveValue={readValue}
                overrideProps={overrides}
                onModelChange={updateWizardData}
                classNameMap={resolvers.resolveClassNameMap}
                componentMap={resolvers.resolveComponentMap}
                callbackMap={resolvers.resolveCallbackMap}
                onValidationChange={onValidate}
            />
        </WizardPage>
    );
}

FNOLContactDetailsPage.propTypes = wizardProps;
export default withViewModelService(FNOLContactDetailsPage);
